import React, { FC } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Link, Paper, Table, TableBody, TableCell, TableRow } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { GroupFragment, GroupRole, UserFeedFollowFragment } from 'api';
import { generateLink } from 'Links';
import { TableCellWithOverflow, UserAvatar } from 'components';
import { useCapabilities } from 'hooks';

import TableHeader from './TableHeader';
import MemberActions from './MemberActions';

interface Props {
  feed_follows: UserFeedFollowFragment[];
  group: GroupFragment;
}

const useStyles = makeStyles(theme => ({
  actionsCell: {
    padding: `16px ${theme.spacing()}px`,
  },
  avatarCell: {
    padding: `${theme.spacing(2)}px 12px ${theme.spacing(2)}px 0`,
  },
  row: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    '&:nth-child(even)': {
      backgroundColor: theme.palette.grey[50],
    },
  },
}));

const MemberTable: FC<Props> = ({ feed_follows, group }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { needs } = useCapabilities();

  const hasActions = needs('Access.Moderator', group.id);

  function getUserRole(role: GroupRole) {
    if (role === GroupRole.Member) {
      return t('common:member');
    }

    if (role === GroupRole.Moderator) {
      return t('groups:roleModerator');
    }

    if (role === GroupRole.Owner) {
      return t('groups:roleOwner');
    }

    return '';
  }

  function renderTable(user: UserFeedFollowFragment, idx: number) {
    const { followed_by_user, group_role } = user;

    return (
      <TableRow key={idx} classes={{ root: classes.row }}>
        {hasActions && (
          <TableCell className={classes.actionsCell}>
            <MemberActions group={group} targetUser={followed_by_user} role={group_role} />
          </TableCell>
        )}
        <TableCell className={hasActions && classes.avatarCell}>
          <UserAvatar link user={followed_by_user} size={40} />
        </TableCell>
        <TableCellWithOverflow padding="none">
          <Link noWrap component={generateLink(`/profile/${followed_by_user.id}`)} variant="body2">
            {followed_by_user.full_name || followed_by_user.email_address}
          </Link>
        </TableCellWithOverflow>
        <TableCellWithOverflow>{getUserRole(group_role)}</TableCellWithOverflow>
        <TableCellWithOverflow>{followed_by_user.job_title}</TableCellWithOverflow>
        <TableCellWithOverflow>{followed_by_user.department_title}</TableCellWithOverflow>
        <TableCellWithOverflow>{followed_by_user.location_title}</TableCellWithOverflow>
      </TableRow>
    );
  }

  return (
    <Paper>
      <Table>
        <TableHeader hasActions={hasActions} />
        <TableBody>{feed_follows.map(renderTable)}</TableBody>
      </Table>
    </Paper>
  );
};

export default MemberTable;
